import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import debug from "@plannel/utils/debug-util";

// old WipLocationPopup의 resourceCd join 구분자(", ") — 백엔드 MasterService의 JOIN_DELIMITER와 동일.
const JOIN_DELIMITER = ", ";

/**
 * 재공 위치(BOR = 공정-설비) 조회/선택 전용 팝업 — old `WipLocationPopup.jsx`(NOBUTTONS, 더블클릭 선택).
 * 더블클릭 시 **선택 행과 같은 공정(routeCd)의 설비를 전부 모아** `resourceCdString`으로 합쳐 돌려준다
 * (재공 1건이 여러 설비에 걸릴 수 있어서 — 백엔드는 이 문자열을 tb_fp_wip_resrc로 역분해해 저장한다).
 *
 * props: open, onClose(), planScope, woCd, onSelect({routeCd, routeNm, resourceCdString})
 */
const WipLocationPopup = ({ t, open, onClose, planScope, woCd, onSelect }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_WIP_LOCATION_SELECT"), viewName: "" }), [t]);

  // 선택 전용 팝업이라 전 컬럼 editable:false 명시(InventoryPopup과 동일 이유).
  const columnDefs = useMemo(() => ([
    { headerName: t("FP_ROUTE_CD"), field: "routeCd", colId: "routeCd", width: 180, editable: false },
    { headerName: t("FP_ROUTE_NM"), field: "routeNm", colId: "routeNm", width: 200, editable: false },
    { headerName: t("FP_RESOURCE_CD"), field: "resourceCd", colId: "resourceCd", width: 180, editable: false },
    { headerName: t("FP_RESOURCE_NM"), field: "resourceNm", colId: "resourceNm", width: 180, editable: false }
  ]), [t]);

  const loadBors = () => {
    if (!planScope) return;
    // old도 호출 셀의 woCd를 함께 보낸다. 다만 BorQueryRepository.makeWhereClause에는 woCd 조건이
    // 없어 old/new 모두 실질적으로 무시된다(계획범위 전체 목록). 백엔드가 조건을 추가하면 그대로
    // 걸리도록 old와 동일하게 계속 보낸다.
    fpCommonService.getBors({ planScope, woCd: woCd ?? "" })
      .then(response => setRowData(isEmpty(response.data) ? [] : response.data))
      .catch(e => { debug(e); setRowData([]); });
  };

  useEffect(() => {
    if (!open || !planScope) return;
    loadBors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope, woCd]);

  // old NOBUTTONS와 동일하게 더블클릭으로만 확정. old와 동일한 집계 규칙:
  // 클릭 행에 routeCd가 있으면 같은 routeCd의 모든 설비를 ", "로 join, 없으면 클릭 행의 설비만.
  const onRowDoubleClicked = (e) => {
    if (!e.data) return;
    const clicked = e.data;
    const resourceCdString = clicked.routeCd
      ? rowData.filter(row => row.routeCd === clicked.routeCd).map(row => row.resourceCd).join(JOIN_DELIMITER)
      : clicked.resourceCd;

    onSelect({
      routeCd: clicked.routeCd,
      routeNm: clicked.routeNm,
      resourceCdString
    });
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t("FP_WIP_LOCATION_SELECT")}</DialogTitle>
      <DialogContent>
        <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowDoubleClicked={onRowDoubleClicked}
            {...gridOptions}
            rowSelection={{ mode: "singleRow", checkboxes: false, enableClickSelection: true }}
          />
        </Box>
      </DialogContent>
    </MuiDialog>
  );
};

export default withTranslation()(WipLocationPopup);
